import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, ROLES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, UserCheck, X as XIcon, Clock, Mail, Send, Trash2, KeyRound, Unlock, Copy, ShieldAlert } from "lucide-react";

const EMPTY = { email: "", name: "", role: "viewer", password: "", active: true };
const EMPTY_INVITE = { email: "", name: "", role: "viewer", message: "" };

const roleLabel = (v) => ROLES.find((r) => r.value === v)?.label || v;

export default function Users() {
    const [rows, setRows] = useState([]);
    const [pending, setPending] = useState([]);
    const [invites, setInvites] = useState([]);
    const [approveRole, setApproveRole] = useState({}); // {uid: role}
    const [open, setOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [inv, setInv] = useState(EMPTY_INVITE);
    const [inviteBusy, setInviteBusy] = useState(false);
    // Temp-password modal after an admin resets someone's password.
    const [resetResult, setResetResult] = useState(null);

    const load = useCallback(async () => {
        const [all, pend, invList] = await Promise.all([
            api.get("/users"),
            api.get("/users/pending"),
            api.get("/users/invites").catch(() => ({ data: { invites: [] } })),
        ]);
        setRows(all.data);
        setPending(pend.data);
        setInvites(invList.data?.invites || []);
    }, []);
    useEffect(() => { load(); }, [load]);

    // Main table excludes pending users (they live in the top approvals section)
    const visibleRows = useMemo(
        () => rows.filter((u) => u.approval_status !== "pending"),
        [rows],
    );

    const setInput = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/users", f);
            toast.success("User created");
            setOpen(false);
            setF(EMPTY);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const resetPassword = async (u) => {
        if (!window.confirm(
            `Reset password for ${u.email}? A one-time password will be generated. ` +
            `They will be forced to change it on next login.`
        )) return;
        try {
            const { data } = await api.post(`/users/${u.id}/reset-password`);
            setResetResult(data);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const unlock = async (u) => {
        try {
            const { data } = await api.post(`/users/${u.id}/unlock`);
            if (data.lockouts_cleared > 0) {
                toast.success(`Unlocked ${u.email} — user can log in immediately.`);
            } else {
                toast.info(`${u.email} wasn't locked out.`);
            }
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const copyTempPassword = async () => {
        try {
            await navigator.clipboard.writeText(resetResult?.temp_password || "");
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Couldn't copy — please select and copy manually.");
        }
    };

    const toggleRole = async (u, role) => {
        try {
            await api.patch(`/users/${u.id}`, { role });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const toggleActive = async (u) => {
        try {
            if (u.active) await api.delete(`/users/${u.id}`);
            else await api.patch(`/users/${u.id}`, { active: true });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const approve = async (u) => {
        const role = approveRole[u.id] || "viewer";
        try {
            await api.post(`/users/${u.id}/approve`, { role });
            toast.success(`${u.email} approved as ${roleLabel(role)}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const reject = async (u) => {
        if (!window.confirm(`Reject access for ${u.email}? They can request again by signing in with Google.`)) return;
        try {
            await api.post(`/users/${u.id}/reject`);
            toast.success(`${u.email} rejected`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const sendInvite = async (e) => {
        e.preventDefault();
        setInviteBusy(true);
        try {
            const { data } = await api.post("/users/invite", inv);
            if (data.invite?.email_delivery?.sent) {
                toast.success(`Invite emailed to ${inv.email}`);
            } else {
                toast.success(`Invite created for ${inv.email}. Email delivery is pending — copy the invite link from the panel.`);
            }
            setInv(EMPTY_INVITE);
            setInviteOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setInviteBusy(false);
        }
    };

    const revokeInvite = async (i) => {
        if (!window.confirm(`Revoke the invite for ${i.email}? They won't be able to accept it after this.`)) return;
        try {
            await api.delete(`/users/invites/${i.id}`);
            toast.success("Invite revoked");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="users-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Users &amp; Roles</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Access control.</h1>
                    <p className="text-sm text-stone-500 mt-3">{visibleRows.length} users</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button variant="outline" className="border-stone-300" data-testid="add-user-btn"><Plus size={16} className="mr-1.5" /> Add user</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-display text-2xl">New user</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <div><Label className="eyebrow">Name</Label><Input required value={f.name} onChange={setInput("name")} className="mt-1.5" /></div>
                            <div><Label className="eyebrow">Email</Label><Input type="email" required value={f.email} onChange={setInput("email")} className="mt-1.5" /></div>
                            <div><Label className="eyebrow">Password</Label><Input type="password" required minLength={6} value={f.password} onChange={setInput("password")} className="mt-1.5" /></div>
                            <div>
                                <Label className="eyebrow">Role</Label>
                                <Select value={f.role} onValueChange={set("role")}>
                                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button type="submit" className="w-full bg-stone-900 hover:bg-stone-800 text-white">Create user</Button>
                        </form>
                    </DialogContent>
                </Dialog>
                <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="invite-user-btn"><Mail size={16} className="mr-1.5" /> Invite user</Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-display text-2xl">Invite by email</DialogTitle></DialogHeader>
                        <form onSubmit={sendInvite} className="space-y-3" data-testid="invite-form">
                            <div><Label className="eyebrow">Their name</Label><Input required value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} className="mt-1.5" data-testid="invite-name" /></div>
                            <div><Label className="eyebrow">Their email</Label><Input type="email" required value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} className="mt-1.5" data-testid="invite-email" /></div>
                            <div>
                                <Label className="eyebrow">Role</Label>
                                <Select value={inv.role} onValueChange={(v) => setInv({ ...inv, role: v })}>
                                    <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="eyebrow">Personal message <span className="normal-case text-[10px] text-stone-500">(optional)</span></Label>
                                <Input maxLength={500} value={inv.message} onChange={(e) => setInv({ ...inv, message: e.target.value })} placeholder="e.g. Welcome to the team!" className="mt-1.5" data-testid="invite-message" />
                            </div>
                            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-[11px] text-blue-800">
                                An email will be sent with a secure 7-day link. They choose their own password when they accept.
                            </div>
                            <Button type="submit" disabled={inviteBusy} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="invite-submit">
                                <Send size={13} className="mr-1.5" /> {inviteBusy ? "Sending…" : "Send invitation"}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Pending invites — visible only when at least one is outstanding */}
            {invites.length > 0 && (
                <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50/60 overflow-hidden" data-testid="invites-section">
                    <div className="px-4 py-3 flex items-center gap-2.5 border-b border-blue-200 bg-blue-100/50">
                        <div className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0">
                            <Mail size={14} strokeWidth={2.2} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-blue-900">Pending invites · {invites.length}</div>
                            <div className="text-[12px] text-blue-800">Waiting for the invitee to accept. Links expire 7 days after sending.</div>
                        </div>
                    </div>
                    <div className="divide-y divide-blue-200">
                        {invites.map((i) => (
                            <div key={i.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`invite-row-${i.id}`}>
                                <div className="w-10 h-10 rounded-full bg-blue-200 text-blue-900 flex items-center justify-center font-medium text-sm shrink-0">
                                    {(i.name || i.email || "?").slice(0, 1).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-stone-900 truncate">{i.name || i.email}</div>
                                    <div className="text-[12px] text-stone-500 truncate">{i.email} · {roleLabel(i.role)}</div>
                                    <div className="text-[11px] text-stone-400 mt-0.5">
                                        Sent {new Date(i.created_at).toLocaleDateString()} · expires {new Date(i.expires_at).toLocaleDateString()}
                                        {i.email_delivery?.sent === false && <span className="ml-2 text-amber-700">· email delivery pending</span>}
                                    </div>
                                </div>
                                <Button size="sm" variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-100 gap-1" onClick={() => revokeInvite(i)} data-testid={`revoke-invite-${i.id}`}>
                                    <Trash2 size={13} /> Revoke
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Pending approvals — visible only when there is at least one */}
            {pending.length > 0 && (
                <div
                    className="mb-6 rounded-xl border border-amber-200 bg-amber-50/60 overflow-hidden"
                    data-testid="pending-approvals-section"
                >
                    <div className="px-4 py-3 flex items-center gap-2.5 border-b border-amber-200 bg-amber-100/50">
                        <div className="w-7 h-7 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
                            <Clock size={14} strokeWidth={2.2} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-amber-900">
                                Pending approvals · {pending.length}
                            </div>
                            <div className="text-[12px] text-amber-800">
                                New Google sign-ins waiting for you. Choose a role and approve — or reject.
                            </div>
                        </div>
                    </div>
                    <div className="divide-y divide-amber-200">
                        {pending.map((u) => (
                            <div
                                key={u.id}
                                className="p-4 flex flex-wrap items-center gap-3"
                                data-testid={`pending-row-${u.id}`}
                            >
                                {u.picture ? (
                                    <img
                                        src={u.picture}
                                        alt=""
                                        className="w-10 h-10 rounded-full object-cover border border-amber-200 shrink-0"
                                        referrerPolicy="no-referrer"
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center font-medium text-sm shrink-0">
                                        {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-stone-900 truncate">{u.name || "—"}</div>
                                    <div className="text-[12px] text-stone-500 truncate">{u.email}</div>
                                    <div className="text-[11px] text-stone-400 mt-0.5">
                                        Requested {new Date(u.created_at).toLocaleString()}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Select
                                        value={approveRole[u.id] || "viewer"}
                                        onValueChange={(v) => setApproveRole((s) => ({ ...s, [u.id]: v }))}
                                    >
                                        <SelectTrigger className="h-9 w-[160px] text-xs bg-white" data-testid={`pending-role-${u.id}`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ROLES.map((r) => (
                                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button
                                        size="sm"
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                                        onClick={() => approve(u)}
                                        data-testid={`approve-btn-${u.id}`}
                                    >
                                        <UserCheck size={14} /> Approve
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-stone-300 text-stone-700 hover:bg-stone-100 gap-1"
                                        onClick={() => reject(u)}
                                        data-testid={`reject-btn-${u.id}`}
                                    >
                                        <XIcon size={14} /> Reject
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th><th className="text-right">Actions</th></tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((u) => (
                                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                    <td className="font-medium">{u.name}</td>
                                    <td>
                                        <div className="flex items-center gap-1.5">
                                            <span>{u.email}</span>
                                            {u.is_locked && (
                                                <span
                                                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200"
                                                    title={`${u.failed_attempts || 0} failed attempts`}
                                                    data-testid={`user-locked-${u.id}`}
                                                >
                                                    <ShieldAlert size={9} /> Locked
                                                </span>
                                            )}
                                            {u.must_change_password && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                                                      title="User must change password on next login">
                                                    <KeyRound size={9} /> Pending reset
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <Select value={u.role} onValueChange={(v) => toggleRole(u, v)}>
                                            <SelectTrigger className="h-8 w-[180px] text-xs"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td>{u.active ? <span className="text-emerald-700 text-xs">Active</span> : <span className="text-stone-500 text-xs">Deactivated</span>}</td>
                                    <td className="text-xs text-stone-500">{new Date(u.created_at).toLocaleDateString()}</td>
                                    <td>
                                        <div className="flex items-center gap-1.5 justify-end">
                                            {u.is_locked && (
                                                <Button variant="outline" size="sm"
                                                        onClick={() => unlock(u)}
                                                        className="border-amber-300 text-amber-800 hover:bg-amber-50 gap-1"
                                                        data-testid={`user-unlock-${u.id}`}>
                                                    <Unlock size={12} /> Unlock
                                                </Button>
                                            )}
                                            <Button variant="outline" size="sm"
                                                    onClick={() => resetPassword(u)}
                                                    className="gap-1"
                                                    data-testid={`user-reset-pw-${u.id}`}>
                                                <KeyRound size={12} /> Reset password
                                            </Button>
                                            <Button variant="outline" size="sm" onClick={() => toggleActive(u)}>{u.active ? "Deactivate" : "Reactivate"}</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Temp password reveal — shown ONCE right after an admin
                resets someone's password. The plaintext is intentionally
                only in memory / clipboard, never persisted. */}
            <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
                <DialogContent className="max-w-md" data-testid="reset-password-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl flex items-center gap-2">
                            <KeyRound size={18} className="text-amber-700" />
                            Password reset for {resetResult?.email}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="text-[13px] text-stone-600">
                            Share this one-time password with the user over a trusted channel
                            (WhatsApp, in person, secure DM). They&apos;ll be forced to change it
                            on next login.
                        </div>
                        <div className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 p-3 flex items-center justify-between gap-2">
                            <code
                                className="font-mono text-[16px] tracking-wide text-stone-900 select-all"
                                data-testid="reset-password-temp"
                            >
                                {resetResult?.temp_password}
                            </code>
                            <Button variant="outline" size="sm"
                                    onClick={copyTempPassword}
                                    className="shrink-0 gap-1"
                                    data-testid="reset-password-copy">
                                <Copy size={12} /> Copy
                            </Button>
                        </div>
                        {resetResult?.lockouts_cleared > 0 && (
                            <div className="text-[11.5px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-center gap-2">
                                <Unlock size={12} />
                                Cleared {resetResult.lockouts_cleared} brute-force lockout entr{resetResult.lockouts_cleared === 1 ? "y" : "ies"} — they can log in immediately.
                            </div>
                        )}
                        <div className="text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded-md px-3 py-2">
                            <b>Security note:</b> VAULT never stores this password in
                            plaintext or emails it. Closing this dialog erases it from
                            memory — copy it now.
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <Button onClick={() => setResetResult(null)}
                                className="bg-stone-900 text-white"
                                data-testid="reset-password-done">
                            I&apos;ve shared it — close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
